/**
 * Debug utilities to help troubleshoot state management and UI updates
 */

/**
 * Logs the before and after state of an object to the console in a formatted way
 * Useful for debugging Redux state changes
 * 
 * @param name The name/label for this debug log
 * @param before The object state before changes
 * @param after The object state after changes 
 */
export const logStateChange = (name: string, before: any, after: any) => {
  // Skip if not in development
  if (process.env.NODE_ENV !== 'development') {
    return;
  }
  
  // Create a group in the console for better readability
  console.group(`🔍 ${name} State Change`);
  console.log('BEFORE:', before);
  console.log('AFTER:', after);
  
  // Calculate diff for simple properties
  if (before && after) {
    const changedProps: Record<string, {before: any, after: any}> = {};
    
    // Find changed properties
    const allKeys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
    
    allKeys.forEach(key => {
      const beforeVal = before[key];
      const afterVal = after[key];
      
      // Only add if values are different
      if (JSON.stringify(beforeVal) !== JSON.stringify(afterVal)) {
        changedProps[key] = {
          before: beforeVal,
          after: afterVal
        };
      }
    });
    
    if (Object.keys(changedProps).length > 0) {
      console.log('CHANGES:', changedProps);
    } else {
      console.log('No changes detected (values may be equal but references different)');
    }
  }
  
  console.groupEnd();
};

/**
 * Logs Redux state for debugging with before and after states
 * Can be subscribed to the Redux store to track all changes
 */
export class ReduxLogger {
  private prevState: any = null;
  
  /**
   * Handle a Redux state change
   * @param currentState The current Redux state
   */
  handleChange(currentState: any) {
    if (!this.prevState) {
      this.prevState = currentState;
      return;
    }
    
    // Log the entire state change
    logStateChange('Redux Store', this.prevState, currentState);
    
    // Update previous state
    this.prevState = currentState;
  }
  
  /**
   * Reset the logger
   */
  reset() {
    this.prevState = null;
  }
}

// Create a singleton instance for app-wide use
export const reduxLogger = new ReduxLogger();
